import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../services/api';
import './TeamStoreCheckInOut.css';

const MODES = {
  'check-in': { label: 'Check In', endpoint: '/team-stores/pickup/check-in' },
  'check-out': { label: 'Check Out', endpoint: '/team-stores/pickup/check-out' },
};

const TeamStoreCheckInOut = () => {
  const [activeTab, setActiveTab] = useState('check-in'); // 'check-in' | 'check-out' | 'history'
  const [code, setCode] = useState('');
  const [result, setResult] = useState(null); // { kind: 'success'|'error'|'warning', message, order }
  const [submitting, setSubmitting] = useState(false);
  const inputRef = useRef(null);

  const [stores, setStores] = useState([]);
  const [pending, setPending] = useState([]);
  const [pendingLoading, setPendingLoading] = useState(true);
  const [awaiting, setAwaiting] = useState([]);
  const [awaitingLoading, setAwaitingLoading] = useState(true);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [historyStoreFilter, setHistoryStoreFilter] = useState('');

  const focusInput = useCallback(() => {
    // Scanner gun is a USB keyboard-wedge device -- it just types into whatever
    // has focus. Keeping this field focused is what makes scanning work at all.
    requestAnimationFrame(() => inputRef.current?.focus());
  }, []);

  const loadPending = useCallback(async () => {
    setPendingLoading(true);
    try {
      const res = await api.get('/team-stores/pickup/pending');
      setPending(res.data.orders || []);
    } catch (err) {
      console.error('Failed to load pending check-in list:', err);
    } finally {
      setPendingLoading(false);
    }
  }, []);

  const loadAwaiting = useCallback(async () => {
    setAwaitingLoading(true);
    try {
      const res = await api.get('/team-stores/pickup/awaiting');
      setAwaiting(res.data.orders || []);
    } catch (err) {
      console.error('Failed to load awaiting-pickup list:', err);
    } finally {
      setAwaitingLoading(false);
    }
  }, []);

  const loadHistory = useCallback(async (storeSlug) => {
    setHistoryLoading(true);
    try {
      const res = await api.get('/team-stores/pickup/history', {
        params: storeSlug ? { store_slug: storeSlug } : {},
      });
      setHistory(res.data.history || []);
    } catch (err) {
      console.error('Failed to load history:', err);
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  useEffect(() => {
    api.get('/team-stores/pickup/stores')
      .then((res) => setStores(res.data.stores || []))
      .catch((err) => console.error('Failed to load store list:', err));
  }, []);

  useEffect(() => {
    if (activeTab === 'check-in') {
      loadPending();
      focusInput();
    } else if (activeTab === 'check-out') {
      loadAwaiting();
      focusInput();
    } else if (activeTab === 'history') {
      loadHistory(historyStoreFilter);
    }
    setResult(null);
    setCode('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === 'history') loadHistory(historyStoreFilter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [historyStoreFilter]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const scanned = code.trim();
    if (!scanned || submitting || activeTab === 'history') return;

    setSubmitting(true);
    setResult(null);

    try {
      const res = await api.post(MODES[activeTab].endpoint, { code: scanned });
      setResult({ kind: 'success', message: res.data.message, order: res.data.order });
      if (activeTab === 'check-in') loadPending();
      else loadAwaiting();
    } catch (err) {
      const data = err.response?.data;
      // 409 = already checked in/out or not checked in yet -- expected states, not failures.
      const kind = err.response?.status === 409 ? 'warning' : 'error';
      setResult({
        kind,
        message: data?.message || 'Something went wrong scanning that barcode.',
        order: data?.order || null,
      });
    } finally {
      setSubmitting(false);
      setCode('');
      focusInput();
    }
  };

  // Staff computers aren't guaranteed to be set to Central Time, and this is a Kerrville, TX
  // store log -- always render in Texas time regardless of the viewer's own device/browser.
  const formatDate = (d) => (d
    ? new Date(d).toLocaleString('en-US', {
        timeZone: 'America/Chicago', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', timeZoneName: 'short',
      })
    : '—');

  return (
    <div className="wcc-container">
      <div className="wcc-header">
        <h1>Team Store Checkin/Checkout</h1>
        <p>Scan the barcode on a store-pickup order's invoice to log it in or out.</p>
      </div>

      <div className="wcc-tabs">
        <button type="button" className={`wcc-tab ${activeTab === 'check-in' ? 'active' : ''}`} onClick={() => setActiveTab('check-in')}>
          Check In
        </button>
        <button type="button" className={`wcc-tab ${activeTab === 'check-out' ? 'active' : ''}`} onClick={() => setActiveTab('check-out')}>
          Check Out
        </button>
        <button type="button" className={`wcc-tab ${activeTab === 'history' ? 'active' : ''}`} onClick={() => setActiveTab('history')}>
          History
        </button>
      </div>

      {activeTab !== 'history' && (
        <>
          <form className="wcc-scan-form" onSubmit={handleSubmit}>
            <input
              ref={inputRef}
              type="text"
              className="wcc-scan-input"
              placeholder="Scan or type barcode (TSO-####)…"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              disabled={submitting}
              autoComplete="off"
            />
            <button type="submit" className={`wcc-submit-btn wcc-submit-${activeTab}`} disabled={submitting || !code.trim()}>
              {submitting ? 'Working…' : MODES[activeTab].label}
            </button>
          </form>

          {result && (
            <div className={`wcc-result wcc-result-${result.kind}`}>
              <div className="wcc-result-message">{result.message}</div>
              {result.order && (
                <div className="wcc-result-detail">
                  <span className="wcc-result-customer">{result.order.customerName}</span>
                  <span className="wcc-result-store">{result.order.storeName} · Order #{result.order.orderNumber}</span>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {activeTab === 'check-in' && (
        <div className="wcc-list-section">
          <h2>Orders pending check-in ({pending.length})</h2>
          {pendingLoading ? (
            <p className="wcc-list-empty">Loading…</p>
          ) : pending.length === 0 ? (
            <p className="wcc-list-empty">Nothing pending — every live store-pickup order has been checked in.</p>
          ) : (
            <table className="wcc-table">
              <thead>
                <tr><th>Customer</th><th>Store</th><th>Order #</th><th>Order date</th></tr>
              </thead>
              <tbody>
                {pending.map((o) => (
                  <tr key={o.teamStoreOrderId}>
                    <td>{o.customerName}</td>
                    <td>{o.storeName}</td>
                    <td>{o.orderNumber}</td>
                    <td>{formatDate(o.orderDate)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {activeTab === 'check-out' && (
        <div className="wcc-list-section">
          <h2>Currently checked in, awaiting pickup ({awaiting.length})</h2>
          {awaitingLoading ? (
            <p className="wcc-list-empty">Loading…</p>
          ) : awaiting.length === 0 ? (
            <p className="wcc-list-empty">Nothing checked in and waiting for pickup right now.</p>
          ) : (
            <table className="wcc-table">
              <thead>
                <tr><th>Customer</th><th>Store</th><th>Order #</th><th>Checked in</th><th>By</th></tr>
              </thead>
              <tbody>
                {awaiting.map((o) => (
                  <tr key={o.teamStoreOrderId}>
                    <td>{o.customerName}</td>
                    <td>{o.storeName}</td>
                    <td>{o.orderNumber}</td>
                    <td>{formatDate(o.checkedInAt)}</td>
                    <td>{o.checkedInBy || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {activeTab === 'history' && (
        <div className="wcc-list-section">
          <div className="wcc-history-header">
            <h2>Check-in / check-out history</h2>
            <select
              className="wcc-store-filter"
              value={historyStoreFilter}
              onChange={(e) => setHistoryStoreFilter(e.target.value)}
            >
              <option value="">All stores</option>
              {stores.map((s) => (
                <option key={s.slug} value={s.slug}>{s.name}</option>
              ))}
            </select>
          </div>
          {historyLoading ? (
            <p className="wcc-list-empty">Loading…</p>
          ) : history.length === 0 ? (
            <p className="wcc-list-empty">No check-in/check-out activity yet.</p>
          ) : (
            <table className="wcc-table">
              <thead>
                <tr>
                  <th>Customer</th><th>Store</th><th>Order #</th><th>Status</th>
                  <th>Checked in</th><th>By</th><th>Checked out</th><th>By</th>
                </tr>
              </thead>
              <tbody>
                {history.map((h) => (
                  <tr key={h.teamStoreOrderId}>
                    <td>{h.customerName}</td>
                    <td>{h.storeName}</td>
                    <td>{h.orderNumber}</td>
                    <td>
                      <span className={`wcc-status-pill wcc-status-${h.status}`}>
                        {h.status === 'checked_out' ? 'Picked up' : 'Awaiting pickup'}
                      </span>
                    </td>
                    <td>{formatDate(h.checkedInAt)}</td>
                    <td>{h.checkedInBy || '—'}</td>
                    <td>{formatDate(h.checkedOutAt)}</td>
                    <td>{h.checkedOutBy || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
};

export default TeamStoreCheckInOut;
